/**
 * SkeletonPage — Full-page skeleton loading state.
 * Used instead of spinners (v4.8 spec).
 * Mimics the app layout with pulsing placeholder blocks.
 */

export function SkeletonPage(): React.ReactElement {
  return (
    <div className="flex min-h-screen bg-surface-page dark:bg-dark-page">
      {/* Sidebar skeleton */}
      <div className="hidden md:flex flex-col w-[260px] glass-sidebar p-4 gap-3">
        {/* Logo area */}
        <div className="h-8 w-32 rounded-button bg-black/5 dark:bg-white/5 animate-pulse" />
        <div className="h-px bg-border-light dark:bg-border-dark my-2" />
        {/* Nav items */}
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="flex items-center gap-3 px-2">
            <div className="h-5 w-5 rounded bg-black/5 dark:bg-white/5 animate-pulse" />
            <div
              className="h-4 rounded bg-black/5 dark:bg-white/5 animate-pulse"
              style={{ width: `${60 + Math.random() * 60}px` }}
            />
          </div>
        ))}
      </div>

      {/* Main content skeleton */}
      <div className="flex-1 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="h-7 w-48 rounded-button bg-black/5 dark:bg-white/5 animate-pulse" />
          <div className="flex gap-3">
            <div className="h-9 w-9 rounded-full bg-black/5 dark:bg-white/5 animate-pulse" />
            <div className="h-9 w-9 rounded-full bg-black/5 dark:bg-white/5 animate-pulse" />
          </div>
        </div>

        {/* Content cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="glass-card p-6 space-y-3">
              <div className="h-4 w-24 rounded bg-black/5 dark:bg-white/5 animate-pulse" />
              <div className="h-8 w-32 rounded bg-black/5 dark:bg-white/5 animate-pulse" />
              <div className="h-3 w-full rounded bg-black/5 dark:bg-white/5 animate-pulse" />
              <div className="h-3 w-3/4 rounded bg-black/5 dark:bg-white/5 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * SkeletonCard — Inline skeleton for individual cards / modules.
 */
export function SkeletonCard(): React.ReactElement {
  return (
    <div className="glass-card p-6 space-y-3 animate-pulse">
      <div className="h-4 w-24 rounded bg-black/5 dark:bg-white/5" />
      <div className="h-8 w-32 rounded bg-black/5 dark:bg-white/5" />
      <div className="h-3 w-full rounded bg-black/5 dark:bg-white/5" />
      <div className="h-3 w-3/4 rounded bg-black/5 dark:bg-white/5" />
    </div>
  );
}

/**
 * SkeletonTable — Skeleton for table/list views.
 */
export function SkeletonTable({ rows = 5 }: { readonly rows?: number }): React.ReactElement {
  return (
    <div className="glass-card p-4 space-y-3 animate-pulse">
      {/* Table header */}
      <div className="flex gap-4 pb-3 border-b border-border-light dark:border-border-dark">
        {Array.from({ length: 4 }, (_, i) => (
          <div
            key={i}
            className="h-3 rounded bg-black/5 dark:bg-white/5"
            style={{ width: `${60 + Math.random() * 80}px` }}
          />
        ))}
      </div>
      {/* Table rows */}
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex gap-4 py-2">
          {Array.from({ length: 4 }, (_, j) => (
            <div
              key={j}
              className="h-4 rounded bg-black/5 dark:bg-white/5"
              style={{ width: `${40 + Math.random() * 100}px` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
